import { getOriginalKnobStar } from './originalKnobStar';
import { sceneResolution, withDeadline } from './renderPolicy';
import { getRemainingTransactions } from './remainingTransactions';
import { Application, Assets, CanvasTextMetrics, Container, Graphics, Rectangle, Sprite, Text } from 'pixi.js';
import { unlockAudio, playStarCellReveal, playStarLineReveal, playOverdriveBurst, playLeverChargeStart, playLeverChargeUpdate, playLeverReleaseBoost } from './sound.js';
import { logRewardDebug } from './rewardDebug.js';
import btcUrl from '../assets/symbols/btc.png';
import ethUrl from '../assets/symbols/eth.png';
import xmrUrl from '../assets/symbols/xmr.png';
import punkUrl from '../assets/symbols/punk.png';
import dogeUrl from '../assets/symbols/doge.png';
import sushiUrl from '../assets/symbols/sushi.png';
import {
  BOARD_HEIGHT,
  BOARD_LEFT,
  BOARD_TOP,
  BOARD_WIDTH,
  BOARD_X,
  BOARD_Y,
  CELL_GAP_X,
  CELL_GAP_Y,
  COLUMNS,
  GAME_HEIGHT,
  GAME_WIDTH,
  LEVER_PANEL_HEIGHT,
  LEVER_PANEL_WIDTH,
  LEVER_PANEL_X,
  LEVER_PANEL_Y,
  LEVER_ROOT_X,
  LEVER_ROOT_Y,
  ROW_LEFT,
  ROW_WIDTH,
  ROWS,
  STAR_SYMBOL,
  SYMBOL_SIZE,
  SYMBOLS,
} from './slotConfig.js';

const SPIN_SYMBOLS = [...SYMBOLS];

const SYMBOL_TEXTURE_URLS = {
  BTC: btcUrl,
  ETH: ethUrl,
  XMR: xmrUrl,
  PUNK: punkUrl,
  DOGE: dogeUrl,
  SUSHI: sushiUrl,
};


const LINE_LABELS = Object.freeze(['StarTax10', 'StarTax100', 'StarTax1K', 'StarTax10K', 'StarTax100K', 'StarTax1M']);

// Reward milestone color rhythm, mirrored in Pixi so VANGUARD line
// labels behave like the original current receiver labels.
// Durations intentionally get slower by milestone: 10 is fastest, 1M slowest.
const REWARD_LINE_LABEL_COLORS = Object.freeze([
  '#ff0000',
  '#0fff00',
  '#0080ff',
  '#ff00ff',
  '#ffff00',
  '#00ffff',
  '#ffa500',
  '#ffffff',
  '#ff0099',
  '#00ff00',
  '#ff0000',
]);

const REWARD_LINE_LABEL_TIMING = Object.freeze([
  { duration: 3, delay: 0.2 },
  { duration: 5, delay: 0.4 },
  { duration: 7, delay: 0.6 },
  { duration: 20, delay: 0.8 },
  { duration: 40, delay: 1.0 },
  { duration: 60, delay: 1.2 },
]);

const LINE_LABEL_X = 668;
const REWARD_COUNTER_CENTER_X = (ROW_LEFT + ROW_WIDTH + BOARD_LEFT + BOARD_WIDTH) / 2;
const LINE_LABEL_FONT_SIZE = 16;

let lineTextContext;

// Center the visible glyphs, rather than the font's ascent/descent box.
function lineTextOffset(text) {
  const key = text.text + ':' + text.style.fontSize + ':' + text.style.fontWeight + ':' + text.style.padding;
  if (text.__inkOffsetKey !== key) {
    lineTextContext ||= document.createElement('canvas').getContext('2d');
    lineTextContext.font = text.style.fontWeight + ' ' + text.style.fontSize + 'px "Press Start 2P"';
    const ink = lineTextContext.measureText(text.text || ' ');
    const metrics = CanvasTextMetrics.measureText(text.text || ' ', text.style);
    text.__inkOffset = metrics.height / 2 + text.style.padding - metrics.fontProperties.ascent
      + (ink.actualBoundingBoxAscent - ink.actualBoundingBoxDescent) / 2;
    text.__inkOffsetKey = key;
  }
  return text.__inkOffset;
}

function hexToRgb(hex) {
  const normalized = String(hex || '#ffffff').replace('#', '').trim();
  const value = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized.padEnd(6, 'f').slice(0, 6);
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixHexColors(a, b, t) {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex({
    r: ca.r + (cb.r - ca.r) * t,
    g: ca.g + (cb.g - ca.g) * t,
    b: ca.b + (cb.b - ca.b) * t,
  });
}

function getLineLabel(row, line) {
  if (line?.label) return line.label;
  return LINE_LABELS[row] || `StarTax${line?.milestone || ''}`;
}

function getLineLabelColor(row, elapsed) {
  const timing = REWARD_LINE_LABEL_TIMING[row] || REWARD_LINE_LABEL_TIMING[0];
  const duration = Math.max(0.001, Number(timing.duration || 3));
  const delay = Number(timing.delay || 0);
  const phase = (((Number(elapsed || 0) - delay) % duration) + duration) % duration / duration;
  const segments = REWARD_LINE_LABEL_COLORS.length - 1;
  const scaled = phase * segments;
  const index = Math.min(segments - 1, Math.floor(scaled));
  const nextIndex = Math.min(segments, index + 1);
  const local = scaled - Math.floor(scaled);
  return mixHexColors(REWARD_LINE_LABEL_COLORS[index], REWARD_LINE_LABEL_COLORS[nextIndex], local);
}

function formatNumber(value) {
  return new Intl.NumberFormat('es-ES').format(value ?? 0);
}

function randomSpinSymbol() {
  return SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)];
}

function drawRoundBox(graphics, x, y, w, h, fill, stroke = null, radius = 18) {
  graphics.clear();
  graphics.roundRect(x, y, w, h, radius).fill(fill);
  if (stroke) graphics.roundRect(x, y, w, h, radius).stroke(stroke);
}



function createFruitText(symbol, size = 48) {
  const text = new Text({
    text: symbol,
    style: {
      fontFamily: 'Arial, sans-serif',
      fontSize: size,
      align: 'center',
      dropShadow: {
        color: '#000000',
        blur: 4,
        angle: Math.PI / 4,
        distance: 3,
      },
    },
  });
  text.anchor.set(0.5);
  return text;
}

class SymbolCell {
  constructor({ starTexture, symbolTextures, x, y }) {
    this.starTexture = starTexture;
    this.symbolTextures = symbolTextures;
    this.root = new Container();
    this.root.x = x;
    this.root.y = y;
    this.baseY = y;
    this.active = false;
    this.spinning = false;
    this.spinPhase = 0;
    this.changeTimer = 0;
    this.spinSpeed = 1;
    this.landing = 0;
    this.currentSymbol = randomSpinSymbol();
    this.nextSymbol = randomSpinSymbol();

    this.bg = new Graphics();
    this.content = new Container();
    this.maskShape = new Graphics();
    this.maskShape.roundRect(-SYMBOL_SIZE / 2 + 3, -SYMBOL_SIZE / 2 + 3, SYMBOL_SIZE - 6, SYMBOL_SIZE - 6, 16).fill({ color: 0xffffff, alpha: 0.001 });
    this.content.mask = this.maskShape;

    this.root.addChild(this.bg, this.maskShape, this.content);
    this.setFrame(false);
    this.paintStatic(this.currentSymbol);
  }

  createDisplay(symbol, size = 46) {
    const texture = symbol === STAR_SYMBOL ? this.starTexture : this.symbolTextures?.[symbol];

    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);

      const maxSize = symbol === STAR_SYMBOL ? SYMBOL_SIZE * 0.80 : SYMBOL_SIZE * 0.86;
      const sourceWidth = texture.width || maxSize;
      const sourceHeight = texture.height || maxSize;
      const scale = Math.min(maxSize / sourceWidth, maxSize / sourceHeight);
      sprite.scale.set(scale);

      return sprite;
    }

    return createFruitText(symbol, Math.min(size, Math.round(SYMBOL_SIZE * 0.62)));
  }

  clearContent() {
    while (this.content.children.length) {
      const child = this.content.removeChildAt(0);
      child.destroy?.();
    }
  }

  setFrame(active = false) {
    this.active = active;
    drawRoundBox(
      this.bg,
      -SYMBOL_SIZE / 2,
      -SYMBOL_SIZE / 2,
      SYMBOL_SIZE,
      SYMBOL_SIZE,
      active ? 0x0b3328 : 0x07131d,
      {
        color: active ? 0x2fff32 : 0x078eff,
        width: active ? 4 : 2,
        alpha: active ? 0.96 : 0.62,
      },
      14
    );
  }

  paintStatic(symbol) {
    this.clearContent();
    const display = this.createDisplay(symbol, symbol === STAR_SYMBOL ? 58 : 56);
    display.y = 0;
    this.content.addChild(display);
    this.content.y = 0;
  }

  paintSpinFrame() {
    this.clearContent();
    const prev = this.createDisplay(randomSpinSymbol(), 54);
    const current = this.createDisplay(this.currentSymbol, 58);
    const next = this.createDisplay(this.nextSymbol, 54);
    prev.y = -SYMBOL_SIZE;
    current.y = 0;
    next.y = SYMBOL_SIZE;
    prev.alpha = 0.55;
    next.alpha = 0.75;
    this.content.addChild(prev, current, next);
  }

  setSymbol(symbol, active = false) {
    this.spinning = false;
    this.transforming = 0;
    this.currentSymbol = symbol;
    this.setFrame(active);
    this.paintStatic(symbol);
    this.root.y = this.baseY;
    this.root.scale.set(1);
    this.landing = 0.24;
  }

  beginStarReveal(intensity = 1) {
    this.spinning = false;
    this.transforming = 0.42 * Math.max(0.85, intensity);
    this.starFlash = 0.52 * Math.max(0.9, intensity);
    this.setFrame(true);
  }

  finishStarReveal() {
    this.currentSymbol = STAR_SYMBOL;
    this.paintStatic(STAR_SYMBOL);
    this.transforming = 0;
    this.starFlash = Math.max(this.starFlash, 0.44);
    this.landing = 0.30;
  }

  startSpin(rowIndex, columnIndex, intensity = 1) {
    if (this.active || this.spinning) return;
    this.spinning = true;
    this.spinPhase = 0;
    this.changeTimer = 0;
    this.spinSpeed = (1.2 + rowIndex * 0.08 + columnIndex * 0.05) * Math.max(0.85, intensity);
    this.currentSymbol = randomSpinSymbol();
    this.nextSymbol = randomSpinSymbol();
    this.paintSpinFrame();
  }

  stopSpin() {
    this.spinning = false;
    this.content.y = 0;
    this.landing = 0.24;
  }

  update(delta, t, rowIndex, columnIndex) {
    if (this.transforming > 0) {
      this.transforming = Math.max(0, this.transforming - delta / 60);
      const glitch = Math.sin(t * 95 + rowIndex * 7 + columnIndex * 13);
      const pulse = Math.max(0, this.transforming / 0.28);
      this.root.x = (columnIndex * 0) + this.root.x;
      this.root.y = this.baseY + glitch * 3.2 * pulse;
      this.root.scale.set(1 + Math.abs(glitch) * 0.11 * pulse, 1 - Math.abs(glitch) * 0.045 * pulse);
      this.bg.alpha = 0.72 + Math.abs(glitch) * 0.28;
      return;
    }

    if (this.spinning) {
      // Cambio continuo y claramente visible de símbolos durante todo el spin.
      this.changeTimer += delta * this.spinSpeed;
      this.spinPhase += delta * 0.095 * this.spinSpeed;

      if (this.changeTimer >= 2.4) {
        this.changeTimer = 0;
        this.currentSymbol = this.nextSymbol;
        this.nextSymbol = randomSpinSymbol();
        this.paintSpinFrame();
      }

      const phase = this.changeTimer / 2.4;
      const eased = phase < 0.5 ? 2 * phase * phase : 1 - Math.pow(-2 * phase + 2, 2) / 2;
      this.content.y = eased * SYMBOL_SIZE;

      const wobble = Math.sin(t * 18 + rowIndex * 0.8 + columnIndex * 0.35);
      this.root.scale.set(1 + Math.abs(wobble) * 0.018, 1 + Math.abs(wobble) * 0.012);
      this.root.y = this.baseY + wobble * 2.2;
      this.bg.alpha = 0.88 + Math.abs(wobble) * 0.12;
      return;
    }

    if (this.active) {
      const pulse = (Math.sin(t * 5 + rowIndex * 0.55) + 1) / 2;
      if (this.starFlash > 0) this.starFlash = Math.max(0, this.starFlash - delta / 60);
      const flash = this.starFlash > 0 ? this.starFlash / 0.52 : 0;
      this.bg.alpha = 0.82 + pulse * 0.18;
      this.root.scale.set(1 + pulse * 0.012 + flash * 0.075);
      this.root.y = this.baseY + Math.sin(t * 34 + columnIndex) * flash * 2.4;
      return;
    }

    this.bg.alpha = 1;
    if (this.landing > 0) {
      this.landing = Math.max(0, this.landing - delta / 60);
      const p = this.landing / 0.24;
      const bounce = Math.sin((1 - p) * Math.PI) * p;
      this.root.y = this.baseY + bounce * 7;
      this.root.scale.set(1 + bounce * 0.035, 1 - bounce * 0.018);
    } else {
      this.root.y = this.baseY;
      this.root.scale.set(1);
    }
  }
}

class LeverControl {
  constructor({ texture, onSpin, onModeChange, onEnergyChange, onAutoModeChange, mode = 'BUY', autoMode = false }) {
    this.onSpin = onSpin;
    this.onModeChange = onModeChange;
    this.onEnergyChange = onEnergyChange;
    this.onAutoModeChange = onAutoModeChange;
    this.mode = mode === 'SELL' ? 'SELL' : 'BUY';
    this.autoMode = Boolean(autoMode);
    this.root = new Container();
    this.root.x = LEVER_ROOT_X;
    this.root.y = LEVER_ROOT_Y;
    this.dragging = false;
    this.disabled = false;
    this.spinning = false;
    this.frenzyPhase = 0;
    this.knobY = 0;
    this.targetY = 0;
    this.visualPullPower = 0;
    this.dragStartTargetY = 0;
    this.broken = false;
    this.pullPower = 0;
    this.lastReleasePower = 0;
    this.releaseKick = 0;
    this.brokenBurst = 0;
    this.brokenShardPhase = 0;

    this.rod = new Graphics();
    this.base = new Graphics();
    this.trackGlow = new Graphics();
    this.knob = new Container();
    this.knob.eventMode = 'static';
    this.knob.cursor = 'grab';

    this.switchRoot = new Container();
    this.buyButton = new Container();
    this.sellButton = new Container();
    this.autoButton = new Container();
    this.buyButton.eventMode = 'static';
    this.sellButton.eventMode = 'static';
    this.autoButton.eventMode = 'static';
    this.buyButton.cursor = 'pointer';
    this.sellButton.cursor = 'pointer';
    this.autoButton.cursor = 'pointer';

    this.knobBg = new Graphics();
    this.star = new Sprite(getOriginalKnobStar(texture));
    this.star.anchor.set(0.5);
    this.star.width = 106;
    this.star.height = 106;

    this.knob.addChild(this.knobBg, this.star);
    this.switchRoot.addChild(this.buyButton, this.sellButton, this.autoButton);
    // Energy meter is rendered as a DOM scene overlay so it can be positioned
    // anywhere in the 1920x1080 VANGUARD scene, not only inside the Pixi canvas.
    this.root.addChild(this.trackGlow, this.rod, this.base, this.knob, this.switchRoot);

    this.draw();
    this.bind();
  }

  setSpinHandler(handler) {
    this.onSpin = handler;
  }

  setModeHandler(handler) {
    this.onModeChange = handler;
  }

  setAutoModeHandler(handler) {
    this.onAutoModeChange = handler;
  }

  setEnergyHandler(handler) {
    this.onEnergyChange = handler;
    this.notifyEnergy(true);
  }

  notifyEnergy(force = false) {
    const displayPower = Math.max(0, Math.min(1, this.spinning ? Math.max(0.42, this.lastReleasePower || 0) : this.visualPullPower || this.pullPower || 0));
    const payload = {
      power: displayPower,
      pulling: Boolean(this.dragging),
      spinning: Boolean(this.spinning),
      active: Boolean(this.dragging || this.spinning || displayPower > 0.01),
      mode: this.mode,
      autoMode: this.autoMode,
    };
    const key = `${Math.round(payload.power * 200)}:${payload.pulling ? 1 : 0}:${payload.spinning ? 1 : 0}:${payload.mode}:${payload.autoMode ? 1 : 0}`;
    if (!force && key === this.lastEnergyKey) return;
    this.lastEnergyKey = key;
    this.onEnergyChange?.(payload);
  }

  setMode(mode) {
    this.mode = mode === 'SELL' ? 'SELL' : 'BUY';
    this.draw();
    this.notifyEnergy(true);
  }

  setAutoMode(autoMode) {
    this.autoMode = Boolean(autoMode);
    this.drawModeSwitch();
    this.notifyEnergy(true);
  }

  setDisabled(disabled) {
    this.disabled = Boolean(disabled);
    this.spinning = Boolean(disabled);
    this.knob.alpha = this.spinning ? 0.96 : 1;
    this.knob.cursor = this.disabled ? 'not-allowed' : 'grab';
    this.buyButton.alpha = 1;
    this.sellButton.alpha = 1;
    this.draw();
    this.notifyEnergy(true);
  }

  setBroken(broken) {
    this.broken = Boolean(broken);
    this.root.x = LEVER_ROOT_X + (this.broken ? 6 : 0);
    this.root.y = LEVER_ROOT_Y;
    this.root.rotation = this.broken ? -0.035 : 0;
    this.dragging = false;
    this.knob.cursor = this.disabled ? 'not-allowed' : 'grab';

    if (this.broken) {
      this.brokenBurst = 1;
      this.brokenShardPhase += 1;
      this.knobY = Math.max(this.knobY, 76);
      this.targetY = Math.max(this.targetY, 92);
      this.pullPower = 0;
      this.lastReleasePower = 0;
    } else {
      // Reset every visual pull value when the broken-lever notice is dismissed.
      // Without this, the external DOM energy meter and the Pixi handle can keep
      // the last failed pull charge after the popup closes.
      this.knobY = 0;
      this.targetY = 0;
      this.visualPullPower = 0;
      this.pullPower = 0;
      this.lastReleasePower = 0;
      this.releaseKick = 0;
      this.brokenBurst = 0;
      this.knob.rotation = 0;
      this.knob.scale.set(1, 1);
    }

    this.notifyEnergy(true);
    this.draw();
  }



  bind() {
    this.knob.on('pointerdown', (event) => {
      if (this.disabled) return;
      unlockAudio();
      this.dragging = true;
      this.knob.cursor = 'grabbing';
      this.dragStartGlobalY = event.global.y;
      this.dragStartKnobY = this.knobY;
      this.dragStartTargetY = this.targetY;
      this.pullPower = Math.max(0, Math.min(1, this.targetY / 142));
      this.visualPullPower = Math.max(0, Math.min(1, this.knobY / 142));
      playLeverChargeStart();
      this.notifyEnergy(true);
    });

    this.knob.on('pointermove', (event) => {
      if (!this.dragging || this.disabled) return;
      const delta = event.global.y - this.dragStartGlobalY;
      const rawY = Math.max(0, Math.min(142, this.dragStartTargetY + delta));
      const rawPower = Math.max(0, Math.min(1, rawY / 142));
      // Resistance curve: the handle follows the pull, but gets heavier near the bottom.
      const resistedPower = Math.pow(rawPower, 1.18);
      this.targetY = 142 * resistedPower;
      this.pullPower = resistedPower;
      playLeverChargeUpdate(this.pullPower);
      this.notifyEnergy();
    });

    const release = () => {
      if (!this.dragging) return;
      this.dragging = false;
      this.knob.cursor = this.disabled ? 'not-allowed' : 'grab';
      const releasedPower = Math.max(0, Math.min(1, Math.max(this.targetY, this.knobY) / 142));
      if (!this.disabled && Math.max(this.targetY, this.knobY) > 58) {
        this.lastReleasePower = Math.max(0.28, releasedPower);
        this.releaseKick = 1;
        playLeverReleaseBoost(this.lastReleasePower);
        this.onSpin?.({ pullPower: this.lastReleasePower });
      }
      this.pullPower = 0;
      this.targetY = 0;
      this.visualPullPower = Math.max(0, Math.min(1, this.knobY / 142));
      this.notifyEnergy(true);
    };

    this.knob.on('pointerup', release);
    this.knob.on('pointerupoutside', release);
    this.knob.on('pointercancel', release);

    // Stable hit areas keep hover independent of the redrawn glow and text.
    for (const button of [this.buyButton, this.sellButton, this.autoButton]) {
      button.interactiveChildren = false;
      button.hitArea = new Rectangle(0, 0, 88, 42);
      button.on('pointerenter', () => {
        this.hoveredButton = button;
        this.drawModeSwitch();
      });
      button.on('pointerleave', () => {
        if (this.hoveredButton === button) this.hoveredButton = null;
        this.drawModeSwitch();
      });
    }

    this.buyButton.on('pointertap', () => {
      if (this.disabled) return;
      this.onModeChange?.('BUY');
    });

    this.sellButton.on('pointertap', () => {
      if (this.disabled) return;
      this.onModeChange?.('SELL');
    });

    this.autoButton.on('pointertap', () => {
      if (this.disabled && !this.autoMode) return;
      this.onAutoModeChange?.(!this.autoMode);
    });
  }

  drawModeButton(container, label, active, y) {
    container.removeChildren();
    container.x = -4;
    container.y = y;

    const hovered = !active && !this.disabled && this.hoveredButton === container;
    const isBuy = label === 'BUY';
    const hiddenDuringSpin = this.spinning && ((this.mode === 'BUY' && !isBuy) || (this.mode === 'SELL' && isBuy));
    container.visible = !hiddenDuringSpin;

    if (hiddenDuringSpin) return;

    const pulse = this.spinning ? (Math.sin(this.frenzyPhase * 0.32 + (isBuy ? 0 : 1.45)) + 1) / 2 : 0;
    const flash = this.spinning ? (Math.sin(this.frenzyPhase * 0.91 + (isBuy ? 0.35 : 2.1)) + 1) / 2 : 0;
    const bg = new Graphics();
    const glow = new Graphics();
    const activeFill = isBuy ? 0x109e52 : 0xa7192e;
    const idleFill = isBuy ? 0x062c1a : 0x310711;
    const spinFill = isBuy ? 0x20ff72 : 0xff365a;
    const spinHotFill = isBuy ? 0xb8ffd0 : 0xffd1d8;
    const strokeColor = isBuy ? 0x27d970 : 0xff3b56;
    const spinStroke = isBuy ? 0xa6ffc0 : 0xffb8c2;
    const fillColor = this.spinning && active ? (flash > 0.72 ? spinHotFill : spinFill) : active ? activeFill : idleFill;
    const stroke = this.spinning && active ? spinStroke : strokeColor;
    const glowColor = isBuy ? 0x2fff72 : 0xff4264;

    if (this.spinning && active) {
      glow.roundRect(-7, -7, 102, 56, 16)
        .fill({ color: glowColor, alpha: 0.10 + pulse * 0.18 });
      glow.roundRect(-3, -3, 94, 48, 14)
        .stroke({ color: 0xffffff, width: 2, alpha: 0.22 + flash * 0.44 });
    }

    bg.roundRect(0, 0, 88, 42, 12)
      .fill(fillColor)
      .stroke({ color: stroke, width: active ? 4 : 2, alpha: this.spinning && active ? 0.96 : active ? 0.98 : 0.72 });

    if (this.spinning && active) {
      bg.roundRect(5, 5, 78, 32, 9)
        .stroke({ color: 0xffffff, width: 1, alpha: 0.18 + pulse * 0.28 });
    }

    const text = new Text({
      text: label,
      style: {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: this.spinning && active ? 19 : 18,
        fontWeight: '900',
        fill: '#ffffff',
        align: 'center',
        ...(this.spinning && active ? {
          dropShadow: {
            color: isBuy ? '#2fff72' : '#ff4264',
            blur: 8,
            distance: 0,
            alpha: 0.85,
          },
        } : {}),
      },
    });
    text.anchor.set(0.5);
    text.x = 44;
    text.y = 22;
    container.scale.set(this.spinning && active ? 1 + pulse * 0.035 : hovered ? 1.06 : 1);
    if (hovered) {
      container.x -= 44 * 0.06;
      container.y -= 21 * 0.06;
    }
    container.addChild(glow, bg, text);
  }

  drawAutoButton() {
    this.autoButton.removeChildren();
    this.autoButton.x = -4;
    this.autoButton.y = 476;
    const active = Boolean(this.autoMode);
    const hiddenDuringSpin = this.spinning && !active;
    this.autoButton.visible = !hiddenDuringSpin;
    if (hiddenDuringSpin) return;
    const hovered = !active && !this.disabled && this.hoveredButton === this.autoButton;
    const pulse = active ? (Math.sin(this.frenzyPhase * 0.45) + 1) / 2 : 0;
    const bg = new Graphics();
    const glow = new Graphics();

    if (active) {
      glow.roundRect(-9, -8, 106, 58, 17)
        .fill({ color: 0xfdce2f, alpha: 0.15 + pulse * 0.18 });
      glow.roundRect(-4, -4, 96, 50, 14)
        .stroke({ color: 0xffffff, width: 2, alpha: 0.18 + pulse * 0.42 });
    }

    bg.roundRect(0, 0, 88, 42, 12)
      .fill(active ? 0xfdce2f : 0xe9edf2)
      .stroke({ color: active ? 0xffffff : 0x4b5563, width: active ? 4 : 2, alpha: active ? 0.95 : 0.62 });
    bg.roundRect(5, 5, 78, 32, 9)
      .fill({ color: active ? 0xfff2a3 : 0xd1d5db, alpha: active ? 0.18 + pulse * 0.18 : 0.20 });

    const text = new Text({
      text: 'AUTO',
      style: {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: 18,
        fontWeight: '900',
        fill: '#050608',
        align: 'center',
        letterSpacing: 1,
        ...(active ? {
          dropShadow: {
            color: '#ffffff',
            blur: 7,
            distance: 0,
            alpha: 0.75,
          },
        } : {}),
      },
    });
    text.anchor.set(0.5);
    text.x = 44;
    text.y = 22;
    this.autoButton.scale.set(active ? 1 + pulse * 0.025 : hovered ? 1.04 : 0.98);
    if (hovered) {
      this.autoButton.x -= 44 * 0.06;
      this.autoButton.y -= 21 * 0.06;
    }
    this.autoButton.alpha = this.disabled && !active ? 0.72 : 1;
    this.autoButton.addChild(glow, bg, text);
  }

  drawModeSwitch() {
    this.drawModeButton(this.buyButton, 'BUY', this.mode === 'BUY', 376);
    this.drawModeButton(this.sellButton, 'SELL', this.mode === 'SELL', 426);
    this.drawAutoButton();
  }

  draw() {
    const spinPulse = this.spinning ? (Math.sin(this.frenzyPhase * 0.42) + 1) / 2 : 0;
    const spinFlash = this.spinning ? (Math.sin(this.frenzyPhase * 1.08 + 0.75) + 1) / 2 : 0;
    const spinJitter = this.spinning ? Math.sin(this.frenzyPhase * 0.78) * 1.25 : 0;
    const topX = 40 + (this.broken ? 10 : 0) + spinJitter;
    const topY = 36 + this.knobY + (this.broken ? 8 : 0);
    const baseX = 40;
    const baseY = 296;
    const isBuy = this.mode === 'BUY';
    const modeColor = isBuy ? 0x109e52 : 0xa7192e;
    const modeHotColor = isBuy ? 0x65ff95 : 0xff6a7a;
    const modeGlow = isBuy ? 0x28ff7a : 0xff6a7a;
    const frenzyColor = isBuy ? 0x2fff72 : 0xff4264;

    this.trackGlow.clear();
    this.trackGlow.roundRect(15, 5, 48, 336, 24)
      .fill({ color: this.spinning ? frenzyColor : 0x06466d, alpha: this.spinning ? 0.16 + spinPulse * 0.18 : 0.34 });
    this.trackGlow.roundRect(18, 8, 42, 330, 22)
      .stroke({ color: this.spinning ? 0xffffff : 0x078eff, width: this.spinning ? 2 : 0, alpha: this.spinning ? 0.18 + spinFlash * 0.38 : 0 });
    this.trackGlow.roundRect(27, 18, 24, 310, 14).fill({ color: 0x07131d, alpha: this.spinning ? 0.58 : 0.72 });

    this.rod.clear();
    this.rod.moveTo(topX, topY + 28).lineTo(baseX, baseY).stroke({ color: this.broken ? 0xff3558 : this.spinning ? frenzyColor : 0x64fbff, width: this.spinning ? 18 : 16, alpha: this.broken ? 0.34 : this.spinning ? 0.26 + spinPulse * 0.22 : 0.20 });
    this.rod.moveTo(topX, topY + 28).lineTo(baseX, baseY).stroke({ color: this.broken ? 0xff2d43 : this.spinning && spinFlash > 0.68 ? 0xffffff : 0x1687ff, width: this.spinning ? 10 : 9, alpha: 0.96 });
    this.rod.moveTo(topX - 4, topY + 28).lineTo(baseX - 4, baseY).stroke({ color: 0xffffff, width: 2, alpha: this.spinning ? 0.30 + spinFlash * 0.32 : 0.22 });

    this.base.clear();
    this.base.roundRect(-14, 270, 108, 92, 22)
      .fill(this.spinning ? 0x0b2224 : 0x132535)
      .stroke({ color: this.broken ? 0xff365c : this.spinning ? (spinFlash > 0.72 ? 0xffffff : frenzyColor) : 0x078eff, width: this.broken ? 4 : this.spinning ? 4 : 3, alpha: this.spinning ? 0.92 : 1 });
    if (this.spinning) {
      this.base.roundRect(-21, 263, 122, 106, 26)
        .stroke({ color: frenzyColor, width: 2, alpha: 0.22 + spinPulse * 0.34 });
    }
    this.base.circle(40, 316, 31).fill(0x07131d).stroke({ color: this.spinning ? (spinFlash > 0.68 ? 0xffffff : modeGlow) : modeGlow, width: this.spinning ? 4 : 3, alpha: this.spinning ? 0.72 + spinPulse * 0.24 : 0.55 });
    this.base.circle(40, 316, 17).fill(this.spinning && spinFlash > 0.70 ? modeHotColor : modeColor).stroke({ color: this.spinning ? 0xffffff : 0x7a35ff, width: 4, alpha: this.spinning ? 0.72 + spinPulse * 0.24 : 0.75 });

    this.drawModeSwitch();

    this.knob.x = topX;
    this.knob.y = topY;

    this.knobBg.clear();
    this.knobBg.circle(0, 0, this.spinning ? 50 + spinPulse * 3 : 48)
      .fill(this.broken ? 0xff284f : this.spinning && spinFlash > 0.70 ? 0xffffff : isBuy ? 0x2af66a : 0xff3858)
      .stroke({ color: this.broken ? 0xffffff : this.spinning ? frenzyColor : 0x7a35ff, width: this.spinning ? 10 : 9, alpha: this.spinning ? 0.98 : 1 });
    if (this.spinning) {
      this.knobBg.circle(0, 0, 59).stroke({ color: 0xffffff, width: 2, alpha: 0.20 + spinFlash * 0.42 });
    }

    if (this.broken) {
      const burst = Math.max(0, this.brokenBurst);
      this.knobBg.moveTo(-34, -25).lineTo(-52 - burst * 14, -42 - burst * 8).stroke({ color: 0xfdce2f, width: 4, alpha: 0.68 + burst * 0.24 });
      this.knobBg.moveTo(28, -18).lineTo(48 + burst * 16, -35 + burst * 6).stroke({ color: 0xffffff, width: 3, alpha: 0.58 + burst * 0.30 });
      this.knobBg.moveTo(20, 25).lineTo(38 + burst * 10, 48 + burst * 9).stroke({ color: 0xff365c, width: 4, alpha: 0.54 + burst * 0.28 });
      this.knobBg.circle(-42 - burst * 8, 18 + burst * 4, 5 + burst * 2).fill({ color: 0xfdce2f, alpha: 0.50 + burst * 0.34 });
      this.knobBg.circle(38 + burst * 9, 12 - burst * 4, 4 + burst * 2).fill({ color: 0xffffff, alpha: 0.44 + burst * 0.32 });
      this.star.rotation = -0.22 + Math.sin(this.brokenShardPhase) * 0.06;
      this.star.alpha = 0.62;
    } else {
      this.star.rotation = 0;
      this.star.alpha = 1;
    }
  }

  update(delta) {
    let needsDraw = false;

    if (this.releaseKick > 0) {
      this.releaseKick = Math.max(0, this.releaseKick - delta / 22);
      needsDraw = true;
    }

    if (this.brokenBurst > 0) {
      this.brokenBurst = Math.max(0, this.brokenBurst - delta / 72);
      this.brokenShardPhase += delta * 0.08;
      needsDraw = true;
    }

    const diff = this.targetY - this.knobY;
    const follow = this.dragging ? 0.30 : 0.22;
    if (Math.abs(diff) > 0.2) {
      this.knobY += diff * Math.min(1, delta * follow);
      needsDraw = true;
    } else if (this.knobY !== this.targetY) {
      this.knobY = this.targetY;
      needsDraw = true;
    }
    this.visualPullPower = Math.max(0, Math.min(1, this.knobY / 142));

    if (this.dragging) {
      this.frenzyPhase += delta * (0.7 + this.pullPower * 1.5);
      needsDraw = true;
    }

    if (this.spinning) {
      this.frenzyPhase += delta;
      needsDraw = true;
    }

    if (needsDraw) this.draw();

    const t = performance.now() / 1000;
    if (this.spinning) {
      const rapid = Math.sin(t * 19) * 0.028;
      const slow = Math.sin(t * 7.5) * 0.034;
      const glow = 1.06 + Math.abs(Math.sin(t * 11)) * 0.08;
      this.knob.rotation = rapid + slow;
      this.knob.scale.set(glow, glow * (0.985 + Math.abs(Math.sin(t * 17)) * 0.035));
      this.root.x = LEVER_ROOT_X + (this.broken ? 6 : 0) + Math.sin(t * 23) * 1.1;
      this.root.y = LEVER_ROOT_Y + Math.cos(t * 27) * 0.55;
      return;
    }

    const tension = this.dragging ? Math.max(this.pullPower, this.visualPullPower) : 0;
    const tensionShakeX = this.dragging ? Math.sin(t * (34 + tension * 42)) * (0.45 + tension * 1.55) : 0;
    const tensionShakeY = this.dragging ? Math.cos(t * (29 + tension * 31)) * tension * 0.85 : 0;
    const brokenShakeX = this.broken ? Math.sin(t * 41) * (1.4 + this.brokenBurst * 3.2) : 0;
    const brokenShakeY = this.broken ? Math.cos(t * 37) * (0.8 + this.brokenBurst * 2.2) : 0;
    this.root.x = LEVER_ROOT_X + (this.broken ? 6 : 0) + tensionShakeX + brokenShakeX;
    this.root.y = LEVER_ROOT_Y + tensionShakeY + brokenShakeY;
    const chargeShake = this.dragging ? Math.sin(t * (18 + tension * 28)) * 0.026 * (0.35 + tension) : 0;
    const bounce = Math.sin(t * 6) * 0.014 + chargeShake;
    const autoHum = this.autoMode && !this.dragging && !this.broken ? (Math.sin(t * 5.2) + 1) / 2 : 0;
    const autoShakeX = this.autoMode && !this.dragging && !this.broken ? Math.sin(t * 12.5) * 0.38 : 0;
    const autoShakeY = this.autoMode && !this.dragging && !this.broken ? Math.cos(t * 9.4) * 0.20 : 0;
    if (this.autoMode && !this.dragging && !this.broken) {
      this.root.x += autoShakeX;
      this.root.y += autoShakeY;
    }
    const glow = 1 + Math.sin(t * 4) * 0.035 + (this.dragging ? tension * 0.055 : 0) + this.releaseKick * 0.08 + autoHum * 0.018;
    this.knob.rotation = bounce + (this.autoMode && !this.dragging && !this.broken ? Math.sin(t * 6.8) * 0.006 : 0);
    this.knob.scale.set(glow, glow * (1 - this.releaseKick * 0.035));
  }
}

export class PixiSlotScene {
  constructor({ host, starUrl, onSpin, onModeChange, onLeverEnergyChange, onAutoModeChange, mode = 'BUY', autoMode = false }) {
    this.host = host;
    this.starUrl = starUrl;
    this.onSpin = onSpin;
    this.onModeChange = onModeChange;
    this.onLeverEnergyChange = onLeverEnergyChange;
    this.onAutoModeChange = onAutoModeChange;
    this.mode = mode === 'SELL' ? 'SELL' : 'BUY';
    this.autoMode = Boolean(autoMode);
    this.app = null;
    this.appReady = false;
    this.cells = [];
    this.lineGlows = [];
    this.lineCounters = [];
    this.lever = null;
    this.destroyed = false;
    this.isSpinning = false;
    this.elapsed = 0;
    this.lastState = null;
    this.lastTransactionCounter = null;
    this.lastLineRemaining = new Map();
    this.txHeat = 0;
    this.overdrive = false;
    this.revealedLineKeys = new Set();
    this.revealTimers = [];
    // Rows that are actively revealing or have just completed a STAR reveal.
    // Incoming reward/balance refreshes can arrive while the reveal is running;
    // they must not repaint the row halfway through and leave only 1-2 stars.
    this.activeStarRevealRows = new Map();
    this.heldStarRevealRows = new Map();
    this.tick = this.tick.bind(this);
  }



  async init({ compatible = false, onFailure = () => {} } = {}) {
    this.compatible = compatible;
    this.stageName = "renderer";
    const app = new Application();
    this.app = app;
    await app.init({
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      background: '#000000',
      preference: compatible ? ["canvas"] : ["webgl", "canvas"],
      antialias: !compatible,
      // Bound GPU work by actual display size, with a lower recovery ceiling.
      resolution: sceneResolution(this.host.getBoundingClientRect().width, window.devicePixelRatio, compatible),
      autoDensity: true,
    });

    // StrictMode/HMR may unmount while Application.init is still pending.
    // Only a fully initialized application can safely expose canvas or destroy plugins.
    if (this.destroyed) {
      app.destroy(true, { children: true, texture: false, textureSource: false });
      return;
    }
    this.appReady = true;
    if (app.renderer?.name === 'canvas') {
      compatible = true;
      this.compatible = true;
      const resolution = sceneResolution(this.host.getBoundingClientRect().width, window.devicePixelRatio, true);
      if (resolution !== app.renderer.resolution) app.renderer.resize(GAME_WIDTH, GAME_HEIGHT, resolution);
    }
    this.host.dataset.renderer = app.renderer?.name || "unknown";

    this.host.innerHTML = '';
    this.host.appendChild(this.app.canvas);

    this.canvas = app.canvas;
    this.contextLost = (event) => {
      event.preventDefault();
      app.stop();
      queueMicrotask(() => { if (!this.destroyed) onFailure(new Error('webgl-context-lost')); });
    };
    this.canvas.addEventListener?.('webglcontextlost', this.contextLost);
    this.stageName = 'assets';
    this.starTexture = await withDeadline(Assets.load(this.starUrl), 15000, 'star-image');
    if (this.destroyed) return;
    this.symbolTextures = {};

    await Promise.all(
      Object.entries(SYMBOL_TEXTURE_URLS).map(async ([key, url]) => {
        this.symbolTextures[key] = await withDeadline(Assets.load(url), 15000, 'symbol-' + key);
      })
    );

    if (this.destroyed) return;

    this.stageName = 'font';
    // Font failure must not prevent the playable scene from appearing.
    try { await withDeadline(document.fonts.load('900 16px "Press Start 2P"'), 3000, 'font'); }
    catch (error) { console.warn('StarTax graphics: using fallback font', error.message); }
    if (this.destroyed) return;
    CanvasTextMetrics.clearMetrics();
    this.stageName = "scene";
    this.buildScene();
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.app || this.destroyed) return;
      const resolution = sceneResolution(this.host.getBoundingClientRect().width, window.devicePixelRatio, compatible);
      if (resolution !== this.app.renderer.resolution) {
        try { this.app.renderer.resize(GAME_WIDTH, GAME_HEIGHT, resolution); }
        catch (error) { onFailure(error); }
      }
    });
    this.resizeObserver.observe(this.host);
    this.app.ticker.maxFPS = compatible ? 30 : 60;
    this.app.ticker.add(this.tick);
    this.onVisibility = () => { if (!this.destroyed) document.hidden ? app.stop() : app.start(); };
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onVisibility();
    this.stageName = 'ready';
  }

  buildScene() {
    const stage = this.app.stage;

    const bg = new Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT).fill(0x000000);
    stage.addChild(bg);

    const board = new Graphics();
    board.roundRect(BOARD_LEFT, BOARD_TOP, BOARD_WIDTH, BOARD_HEIGHT, 28).fill(0x061019);
    board.roundRect(BOARD_LEFT, BOARD_TOP, BOARD_WIDTH, BOARD_HEIGHT, 28).stroke({ color: 0x078eff, width: 3, alpha: 0.88 });
    stage.addChild(board);

    for (let row = 0; row < ROWS; row += 1) {
      const rowY = BOARD_Y + row * (SYMBOL_SIZE + CELL_GAP_Y);
      const glow = new Graphics();
      glow.roundRect(ROW_LEFT, rowY - SYMBOL_SIZE / 2 - 6, ROW_WIDTH, SYMBOL_SIZE + 12, 16)
        .fill({ color: 0x132535, alpha: 0.94 })
        .stroke({ color: 0x078eff, width: 1, alpha: 0.18 });
      stage.addChild(glow);
      this.lineGlows.push(glow);

      const counter = new Text({
        text: LINE_LABELS[row] || 'StarTax',
        resolution: this.compatible ? 1 : 2,
        style: {
          fontFamily: 'Press Start 2P, Consolas, Menlo, monospace',
          fontSize: LINE_LABEL_FONT_SIZE,
          padding: 8,
          fontWeight: '900',
          fill: getLineLabelColor(row, 0),
          align: 'left',
          letterSpacing: 0.25,
        },
      });
      counter.anchor.set(0, 0.5);
      counter.x = LINE_LABEL_X;
      counter.y = rowY + lineTextOffset(counter);
      counter.on('pointerenter', () => { counter.__hovered = true; });
      counter.on('pointerleave', () => { counter.__hovered = false; });
      stage.addChild(counter);
      this.lineCounters.push(counter);

      for (let column = 0; column < COLUMNS; column += 1) {
        const x = BOARD_X + column * (SYMBOL_SIZE + CELL_GAP_X) + SYMBOL_SIZE / 2;
        const y = rowY;
        const cell = new SymbolCell({ starTexture: this.starTexture, symbolTextures: this.symbolTextures, x, y });
        stage.addChild(cell.root);
        this.cells.push(cell);
      }
    }

    const leverPanel = new Graphics();
    leverPanel.roundRect(LEVER_PANEL_X, LEVER_PANEL_Y, LEVER_PANEL_WIDTH, LEVER_PANEL_HEIGHT, 28).fill(0x132535).stroke({ color: 0x078eff, width: 3 });
    stage.addChild(leverPanel);

    this.lever = new LeverControl({ texture: this.starTexture, onSpin: this.onSpin, onModeChange: this.onModeChange, onEnergyChange: this.onLeverEnergyChange, onAutoModeChange: this.onAutoModeChange, mode: this.mode, autoMode: this.autoMode });
    stage.addChild(this.lever.root);
  }

  setSpinHandler(handler) {
    this.onSpin = handler;
    this.lever?.setSpinHandler(handler);
  }

  setModeHandler(handler) {
    this.onModeChange = handler;
    this.lever?.setModeHandler(handler);
  }

  setAutoModeHandler(handler) {
    this.onAutoModeChange = handler;
    this.lever?.setAutoModeHandler(handler);
  }

  setLeverEnergyHandler(handler) {
    this.onLeverEnergyChange = handler;
    this.lever?.setEnergyHandler(handler);
  }

  setMode(mode) {
    this.mode = mode === 'SELL' ? 'SELL' : 'BUY';
    this.lever?.setMode(this.mode);
  }

  setAutoMode(autoMode) {
    this.autoMode = Boolean(autoMode);
    this.lever?.setAutoMode(this.autoMode);
  }

  updateLiveCounters(transactionCounter) {
    // Text only: never settle reels or change ownership/reveal state while spinning.
    this.lastState?.lines.forEach((line,row) => {
      const text = this.lineCounters[row];
      if (!line.activeForWallet || !text) return;
      const remaining = getRemainingTransactions(transactionCounter,line.milestone);
      if (remaining == null) return;
      if (this.lastLineRemaining.get(line.key) !== remaining) text.__tickPulse = 1;
      text.text = `${formatNumber(remaining)} TX`;
      text.__maxRewardScale = Math.min(1.095, (2 * (BOARD_LEFT + BOARD_WIDTH - 12 - REWARD_COUNTER_CENTER_X)) / Math.max(1, CanvasTextMetrics.measureText(text.text, text.style).width));
      this.lastLineRemaining.set(line.key,remaining);
    });
  }

  renderState(state) {
    if (!this.app || !this.cells.length) return;

    const counter = Number(state?.transactionCounter || 0);
    if (this.lastTransactionCounter != null && counter > this.lastTransactionCounter) {
      const delta = counter - this.lastTransactionCounter;
      this.txHeat = Math.min(12, this.txHeat + Math.min(6, delta) * 1.45);
      const wasOverdrive = this.overdrive;
      this.overdrive = this.txHeat >= 5.5 || delta >= 3;
      if (this.overdrive && !wasOverdrive) playOverdriveBurst();
    }
    this.lastTransactionCounter = counter;
    this.lastState = state;

    state.lines.forEach((line, row) => {
      const active = Boolean(line.activeForWallet);
      const rowY = BOARD_Y + row * (SYMBOL_SIZE + CELL_GAP_Y);
      const glow = this.lineGlows[row];
      if (glow) {
        glow.clear();
        glow.roundRect(ROW_LEFT, rowY - SYMBOL_SIZE / 2 - 6, ROW_WIDTH, SYMBOL_SIZE + 12, 16)
          .fill({ color: 0x132535, alpha: 0.94 })
          .stroke({ color: 0x078eff, width: 1, alpha: 0.22 });
      }

      const counter = this.lineCounters[row];
      if (counter) {
        const nextCounterText = active ? (line.remaining == null ? '' : `${formatNumber(line.remaining)} TX`) : getLineLabel(row, line);
        const previousRemaining = this.lastLineRemaining.get(line.key);
        const remainingChanged = active && line.remaining != null && previousRemaining != null && previousRemaining !== line.remaining;
        counter.text = nextCounterText;
        counter.anchor.set(active ? 0.5 : 0, 0.5);
        counter.x = active ? REWARD_COUNTER_CENTER_X : LINE_LABEL_X;
        counter.style.fontFamily = 'Press Start 2P, Consolas, Menlo, monospace';
        counter.style.fontWeight = '900';
        counter.style.fontSize = LINE_LABEL_FONT_SIZE;
        counter.style.padding = active ? 16 : 8;
        counter.eventMode = active ? 'static' : 'none';
        if (!active) { counter.__hovered = false; counter.__tickPulse = 0; }
        counter.style.align = active ? 'center' : 'left';
        counter.style.letterSpacing = active ? 0 : 0.25;
        counter.style.fill = active ? '#39ff14' : getLineLabelColor(row, this.elapsed);
        counter.__activeLine = active;
        counter.style.dropShadow = active ? { color: '#39ff14', alpha: 0.95, blur: 12, distance: 0 } : null;
        counter.__maxRewardScale = active
          ? Math.min(1.095, (2 * (BOARD_LEFT + BOARD_WIDTH - 12 - REWARD_COUNTER_CENTER_X)) / Math.max(1, CanvasTextMetrics.measureText(counter.text || ' ', counter.style).width))
          : 1;
        // Cancel Pixi padding/ink offsets in the pivot so prize pulses expand symmetrically.
        const inkOffset = lineTextOffset(counter);
        counter.pivot.set(active ? -counter.style.padding : 0, active ? -inkOffset : 0);
        counter.__baseY = active ? rowY : rowY + inkOffset;
        counter.y = counter.__baseY;
        counter.__rowIndex = row;
        if (remainingChanged) counter.__tickPulse = 1;
        this.lastLineRemaining.set(line.key, active ? line.remaining : null);
      }

      const revealKey = `${line.key}:${line.cycleId ?? counter}:${line.symbols.join('-')}`;
      const activeReveal = this.activeStarRevealRows.get(row);
      const heldReveal = this.heldStarRevealRows.get(row);
      const isSameActiveReveal = Boolean(activeReveal && activeReveal.revealKey === revealKey);
      const isSameHeldReveal = Boolean(heldReveal && heldReveal.revealKey === revealKey);
      const preserveStarRevealVisual = Boolean(active && (isSameActiveReveal || isSameHeldReveal));

      if (preserveStarRevealVisual && !line.revealToStar) {
        logRewardDebug('star-reveal-row-preserved', {
          key: line.key,
          row,
          revealKey,
          activeReveal: isSameActiveReveal,
          heldReveal: isSameHeldReveal,
          remaining: line.remaining ?? null,
          transactionCounter: counter,
        });
      } else {
        line.symbols.forEach((symbol, column) => {
          const cell = this.cells[row * COLUMNS + column];
          cell?.setSymbol(symbol, active);
        });
      }

      if (line.revealToStar && active) {
        if (!this.revealedLineKeys.has(revealKey)) {
          this.revealedLineKeys.add(revealKey);
          this.scheduleStarReveal(row, line, revealKey);
        }
      } else if (!active) {
        this.activeStarRevealRows.delete(row);
        this.heldStarRevealRows.delete(row);
      }
    });
  }


  scheduleStarReveal(row, line, revealKey) {
    const activeLineCount = this.lastState?.lines?.filter((candidate) => candidate.revealToStar && candidate.activeForWallet).length || 1;
    const levelPower = Math.max(1, Math.log10(Math.max(10, Number(line.milestone || 10))));
    const intensity = Math.min(2.4, 0.82 + levelPower * 0.22 + activeLineCount * 0.16);
    const rowDelay = row * 430;
    const columnStep = Math.max(125, 180 - activeLineCount * 8);
    const totalRevealMs = rowDelay + (COLUMNS - 1) * columnStep + 430;

    this.activeStarRevealRows.set(row, {
      revealKey,
      lineKey: line.key,
      cycleId: line.cycleId ?? null,
    });
    this.heldStarRevealRows.delete(row);

    logRewardDebug('star-reveal-row-lock-start', {
      key: line.key,
      row,
      revealKey,
      cycleId: line.cycleId ?? null,
      totalRevealMs,
    });

    playStarLineReveal({ intensity, when: rowDelay / 1000 });

    for (let column = 0; column < COLUMNS; column += 1) {
      const cell = this.cells[row * COLUMNS + column];
      if (!cell) continue;
      const startDelay = rowDelay + column * columnStep;
      const finishDelay = startDelay + 330;

      this.revealTimers.push(setTimeout(() => {
        cell.beginStarReveal(intensity);
        playStarCellReveal({ intensity, column });
      }, startDelay));

      this.revealTimers.push(setTimeout(() => {
        cell.finishStarReveal();
      }, finishDelay));
    }

    this.revealTimers.push(setTimeout(() => {
      const activeReveal = this.activeStarRevealRows.get(row);
      if (activeReveal?.revealKey !== revealKey) return;
      this.activeStarRevealRows.delete(row);
      this.heldStarRevealRows.set(row, {
        revealKey,
        lineKey: line.key,
        cycleId: line.cycleId ?? null,
      });
      logRewardDebug('star-reveal-row-lock-complete', {
        key: line.key,
        row,
        revealKey,
        cycleId: line.cycleId ?? null,
      });
    }, totalRevealMs));
  }

  getSpinIntensity() {
    const pullBoost = Math.min(0.72, Math.max(0, this.lever?.lastReleasePower || 0) * 0.72);
    return Math.min(2.7, 1 + this.txHeat * 0.075 + (this.overdrive ? 0.35 : 0) + pullBoost);
  }

  setSpinning(spinning) {
    this.isSpinning = spinning;
    this.lever?.setDisabled(spinning);

    if (spinning) {
      // A new manual/AUTO lever pull is the canonical moment to leave the STAR
      // celebration state and return active prize rows to their assigned crypto
      // symbol. Passive incoming rewards must not clear the reveal.
      this.activeStarRevealRows.clear();
      this.heldStarRevealRows.clear();

      for (let row = 0; row < ROWS; row += 1) {
        const locked = Boolean(this.lastState?.lines?.[row]?.activeForWallet);
        const line = this.lastState?.lines?.[row];
        for (let column = 0; column < COLUMNS; column += 1) {
          const cell = this.cells[row * COLUMNS + column];
          if (locked) {
            const symbol = line?.symbols?.[column] || line?.revealSymbol;
            if (symbol) cell?.setSymbol(symbol, true);
          } else {
            cell?.startSpin(row, column, this.getSpinIntensity());
          }
        }
      }
    } else {
      this.cells.forEach((cell) => cell.stopSpin());
    }
  }

  setLeverBroken(broken) {
    this.lever?.setBroken(Boolean(broken));
  }

  tick(ticker) {
    if (this.destroyed) return;
    const delta = ticker.deltaTime;
    this.elapsed += delta / 60;
    this.txHeat = Math.max(0, this.txHeat - delta / 90);
    this.overdrive = this.overdrive && this.txHeat > 2.8;
    // Meter config is now consumed by the DOM overlay; keep lever updates isolated.
    this.lever?.update(delta);
    this.lever?.notifyEnergy?.();

    this.lineCounters.forEach((counter) => {
      if (!counter) return;
      const row = Number(counter.__rowIndex || 0);
      const pulse = Math.max(0, Number(counter.__tickPulse || 0));
      if (counter.__activeLine) {
        // Fast, continuous green shimmer; keep the digits readable at every phase.
        const shimmer = (Math.sin(this.elapsed * 15) + 1) / 2;
        const accent = Math.pow((Math.sin(this.elapsed * 7.5) + 1) / 2, 3);
        const hover = counter.__hovered ? 0.025 : 0;
        const kick = pulse > 0 ? Math.sin((1 - pulse) * Math.PI) * 0.035 : 0;
        counter.scale.set(Math.min(counter.__maxRewardScale || 1, 1 + shimmer * 0.02 + accent * 0.015 + hover + kick));
        counter.alpha = 0.9 + shimmer * 0.1;
        counter.__tickPulse = Math.max(0, pulse - delta / 18);
      } else {
        counter.style.fill = getLineLabelColor(row, this.elapsed);
        counter.scale.set(1);
        counter.alpha = 1;
      }
      counter.y = Number(counter.__baseY || counter.y);
    });

    for (let row = 0; row < ROWS; row += 1) {
      for (let column = 0; column < COLUMNS; column += 1) {
        const cell = this.cells[row * COLUMNS + column];
        cell?.update(delta, this.elapsed, row, column);
      }
    }
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.canvas?.removeEventListener?.("webglcontextlost", this.contextLost);
    document.removeEventListener("visibilitychange", this.onVisibility);

    try {
      this.revealTimers.forEach((timer) => clearTimeout(timer));
      this.revealTimers = [];
      this.activeStarRevealRows?.clear?.();
      this.heldStarRevealRows?.clear?.();
      if (this.appReady && this.app) {
        this.app.ticker?.remove(this.tick);
        this.app.destroy(true, { children: true, texture: false, textureSource: false });
      }
    } catch (error) {
      console.warn('Pixi cleanup warning:', error);
    }

    this.app = null;
    this.cells = [];
    this.lineGlows = [];
    this.lineCounters = [];
    this.lever = null;
  }
}
